<template>
    <ResourcePage v-if="event" :theme="theme">
        <template #header>
            <ResourcePageHero :document="event" :hide-image="hasForm" />
        </template>
        <template #main>
            <SliceZone :components="components" :slices="event?.data?.slices ?? []" />
        </template>
        <template #aside>
            <ResourcePageForm v-if="hasForm && isFutureEvent" :title="asText(event.data?.formTitle)" v-bind="formConfig" />
            <TableOfContents v-else :document="event" />
        </template>
    </ResourcePage>
</template>

<script lang="ts" setup>
    import {ref, inject, computed} from 'vue';
    import {asText} from '@prismicio/helpers';
    import {SliceZone} from '@prismicio/vue';
    import type {EventDocument} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {useAsyncData, useHead, useWebsitePageView, usePrismicTypeGuards} from '#imports';
    import {PRISMIC_DOCUMENT} from '~/app/keys';
    import ResourcePage from '~/components/ResourcePage.vue';
    import ResourcePageForm from '~/components/ResourcePageForm.vue';
    import ResourcePageHero from '~/components/ResourcePageHero.vue';
    import TableOfContents from '~/components/TableOfContents.vue';
    import usePrismicDocument from '~/composables/content/usePrismicDocument';
    import {createDateHelpers} from '~/composables/dateHelpers';
    import {components} from '~/slices/components';

    const {
        params: {uid},
    } = useRoute('resources-event-uid');

    const eventDocument = inject(PRISMIC_DOCUMENT, ref());
    const {data: event} = await useAsyncData('event', () => usePrismicDocument<EventDocument>('event', uid as string), {deep: true});
    eventDocument.value = event.value;

    const hasForm = computed(() => !!event.value?.data.hubspotFormId);
    const formConfig = computed(() => {
        return {
            portalId: event.value?.data.hubspotPortalId as string,
            formId: event.value?.data.hubspotFormId as string,
            region: event.value?.data.hubspotRegion as string,
            salesforceId: event.value?.data.salesForceId as string,
            trackingCategory: 'resources-center-events',
        };
    });

    const theme = computed(() => event.value?.data.theme || 'default');

    const {isBefore, isSameDay} = createDateHelpers();
    const {hasProperty} = usePrismicTypeGuards();

    const isEventDocument = (doc: typeof event.value) => {
        return hasProperty(doc?.data, 'eventType') || hasProperty(doc?.data, 'date');
    };

    const isFutureEvent = computed(
        () =>
            isEventDocument(event.value) &&
            (isBefore(new Date(), new Date(eventDocument.value?.data?.date as string)) ||
                isSameDay(new Date(), new Date(eventDocument.value?.data.date as string))),
    );

    useWebsitePageView('resources-center', 'events');

    if (event.value?.data.isPrivate) {
        useHead({meta: [{name: 'robots', content: 'noindex'}]});
    }
</script>
